import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	WritableSignal,
} from '@angular/core'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { finalize, interval, Subject, takeUntil } from 'rxjs'
import { OptionsCalendarParamsInterface } from '@modules/options-module/general/interfaces/options-calendar-params.interface'
import { OptionsService } from '@modules/options-module/general/services/options.service'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { AlertService } from '@shared/services/alert.service'
import { HttpErrorResponse } from '@angular/common/http'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { OptionsHelperService } from '@modules/options-module/general/services/options-helper.service'
import { DatePipe, NgTemplateOutlet } from '@angular/common'
import { TuiSkeleton } from '@taiga-ui/kit'
import { CarouselComponent } from '@shared/components/ui-kit/carousel/carousel.component'
import { OptionsFiltersInterface } from '@modules/options-module/general/interfaces/options-filters.interface'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-options-board-header',
	templateUrl: './options-board-header.component.html',
	styleUrl: './options-board-header.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [TranslatePipe, CarouselComponent, DatePipe, NgTemplateOutlet, TuiSkeleton, ButtonComponent],
})
export class OptionsBoardHeaderComponent implements OnInit, OnDestroy {
	protected readonly skeletons: number[] = [1, 2, 3, 4]
	protected readonly date: DateConstructor = Date
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum

	public liveTicker: InputSignal<LiveTickerInterface | null> = input<LiveTickerInterface | null>(null)

	public isLoadingOptions: OutputEmitterRef<boolean> = output<boolean>()
	public changeFilters: OutputEmitterRef<OptionsFiltersInterface> = output<OptionsFiltersInterface>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _optionsService: OptionsService = inject<OptionsService>(OptionsService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _optionsHelperService: OptionsHelperService = inject<OptionsHelperService>(OptionsHelperService)

	private symbol: Signal<string | null> = computed((): string | null => {
		const liveTicker: LiveTickerInterface | null = this.liveTicker()
		if (!liveTicker?.currentPrices.price) return null

		return liveTicker.ticker
	})

	protected optionsType: WritableSignal<OptionTypeEnum> = signal<OptionTypeEnum>(this.optionTypeEnum.CALL)
	protected dateExpiration: WritableSignal<string | null> = signal<string | null>(null)

	protected calendar: WritableSignal<string[]> = signal<string[]>([])
	protected isLoadingCalendar: WritableSignal<boolean> = signal<boolean>(false)

	constructor() {
		effect((): void => {
			this._getCalendar(this.symbol())
		})
	}

	private _sendFilters(): void {
		const symbol: string | null = this.symbol()
		const dateExpiration: string | null = this.dateExpiration()

		if (!symbol) return

		this.changeFilters.emit({
			symbol,
			contract_type: this.optionsType(),
			expiration_date: dateExpiration,
		})
	}

	private _getCalendar(symbol: string | null): void {
		if (!symbol) return
		const todayDate: string = new this.date().toISOString().slice(0, 10)

		const params: OptionsCalendarParamsInterface = {
			symbol,
			type: this.optionsType(),
			expiration_date_gte: todayDate,
			limit: 10000,
		}

		this.isLoadingCalendar.set(true)
		this._optionsService
			.getCalendar(params)
			.pipe(
				takeUntil(this._destroy$),
				finalize((): void => this.isLoadingCalendar.set(false)),
			)
			.subscribe(
				(response: string[]): void => {
					this.calendar.set(response)
					this.setDateExpiration(this.calendar()[0] ?? null)
					this._check1515ET()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message ??
							this._translateService.instant('optionsBoardHeader.datesExpirations.errorFetch'),
					)
				},
			)
	}

	private _check1515ET(): void {
		const firstDate: string | undefined = this.calendar()[0]
		const reached: boolean = this._optionsHelperService.checkReachedDate1515ET(firstDate)
		if (reached) {
			this.calendar.update((c: string[]): string[] => c.slice(1))
			this.setDateExpiration(this.calendar()[0] ?? null)
			return
		}
	}

	private _runCheck1515ET(): void {
		interval(60000)
			.pipe(takeUntil(this._destroy$))
			.subscribe((): void => {
				this._check1515ET()
			})
	}

	protected setOptionsType(type: OptionTypeEnum): void {
		if (this.optionsType() === type) return
		this.optionsType.set(type)
		this._sendFilters()
	}

	protected setDateExpiration(date: string | null): void {
		this.dateExpiration.set(date)
		this._sendFilters()
	}

	ngOnInit(): void {
		this._runCheck1515ET()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
